import {
  isCompound,
  type OTCRepoProductDetails,
  type QtyInputCurrencyOptions,
  type Security,
  SecurityTypesUtils,
  SubProductTypeEnum,
} from '@talos/kyoko';
import type { WritableDraft } from 'immer';
import { compact, uniq, values } from 'lodash-es';
import type { RFQCurrency, RFQState } from './types';

export const RFQ_UNITS_CURRENCY: RFQCurrency = { type: 'units', currency: undefined, label: 'Units' };
export const RFQ_NONE_CURRENCY: RFQCurrency = { type: 'none', currency: undefined, label: '' };
export const RFQ_CONTRACTS_CURRENCY: RFQCurrency = {
  type: 'contracts',
  currency: undefined,
  label: 'Contracts',
};

export function createRFQNoRequestCurrency(label: string): RFQCurrency {
  return { type: 'none', currency: undefined, label };
}

export function createRFQAssetCurrency(currency: string): RFQCurrency {
  return { type: 'asset', currency, label: currency };
}

export function rfqCurrencyEquals(left: RFQCurrency | undefined, right: RFQCurrency | undefined): boolean {
  if (left == null || right == null) {
    return left === right;
  }

  return getRFQCurrencyOptionKey(left) === getRFQCurrencyOptionKey(right);
}

/** Resolves a unique key for an RFQCurrency value where object identity is not stable. */
export function getRFQCurrencyOptionKey(currency: RFQCurrency): string {
  return `${currency.type}-${currency.currency}`;
}

export function getRFQCurrencyAsset(currency: RFQCurrency | undefined): string | undefined {
  return currency?.type === 'asset' ? currency.currency : undefined;
}

export function isRFQContractsCurrency(currency: RFQCurrency | undefined): boolean {
  return currency?.type === 'units' || currency?.type === 'contracts';
}

export function resolveRFQCurrencyFromEntity(
  entitySecurity: Security | undefined,
  entityCurrency: string | undefined
): RFQCurrency | undefined {
  if (entitySecurity == null) {
    return undefined;
  }

  const availableCurrencies = getRFQFormCurrencySpec(entitySecurity).getAvailableCurrencies(entitySecurity);

  const matchedCurrency = availableCurrencies.find(
    ({ currency }) => getRFQCurrencyAsset(currency) === entityCurrency
  )?.currency;
  if (matchedCurrency) {
    return matchedCurrency;
  } else {
    return availableCurrencies.at(0)?.currency;
  }
}

/** RFQ units/contracts are modeled in UI state, but the quote request API represents both by omitting Currency. */
export function rfqCurrencyToRequestCurrency(currency: RFQCurrency | undefined): string | undefined {
  return getRFQCurrencyAsset(currency);
}

export function getOTCRepoQuantityCurrencyOptions({
  cashCurrency,
  collateralAsset,
}: {
  cashCurrency: string | undefined;
  collateralAsset: string | undefined;
}): QtyInputCurrencyOptions<RFQCurrency>[] {
  const collateralCurrency = collateralAsset ? createRFQNoRequestCurrency(collateralAsset) : undefined;
  const cashRFQCurrency =
    cashCurrency && cashCurrency !== collateralAsset ? createRFQAssetCurrency(cashCurrency) : undefined;

  return compact([collateralCurrency, cashRFQCurrency]).map(currency => ({
    currency,
    label: currency.label,
  }));
}

export function getDefaultOTCRepoRFQCurrency(options: QtyInputCurrencyOptions<RFQCurrency>[]): RFQCurrency | undefined {
  return options.at(0)?.currency;
}

function getOTCRepoProductDetails(security: Security): Partial<OTCRepoProductDetails> | undefined {
  return security.ProductDetails as Partial<OTCRepoProductDetails> | undefined;
}

/**
 * An interface defining the behavior of our different currency "modes" in the RFQ Form.
 * Depending on the different _types_ of securities we can select in the form, there is different behavior around which currencies
 * to default to, which currencies can be selected, etc. This interface collects this logic in one place.
 */
interface RFQFormCurrencySpec {
  type: 'compound' | 'default' | 'predictionMarket' | 'otcRepo';
  /** Used to figure out which currency mode to use for the given security. */
  isMyType: undefined | ((security: Security) => boolean);
  /** To be called when the symbol is changed. Ensures that the currency is set correctly in the state. */
  onSymbolChanged: (state: WritableDraft<RFQState>, security: Security) => void;
  /** Given a security, returns the currency options to provide the user */
  getAvailableCurrencies: (security: Security) => QtyInputCurrencyOptions<RFQCurrency>[];
}

export const RFQFormCurrencies: { [key in RFQFormCurrencySpec['type']]: RFQFormCurrencySpec } = {
  /** Default is for spot and falconx options currently. We support Base and Quote for both these types of instruments. */
  default: {
    type: 'default',
    isMyType: undefined, // default is handled explicitly in getRFQFormCurrencySpec below for simpler typing instead of just returning true here
    onSymbolChanged: (state, security) => {
      const selectedAsset = getRFQCurrencyAsset(state.form.rfqCurrencyField.value);
      const currentSelectionNotValid =
        selectedAsset !== security.BaseCurrency && selectedAsset !== security.QuoteCurrency;
      if (currentSelectionNotValid) {
        state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(
          createRFQAssetCurrency(security.BaseCurrency!),
          true
        );
      }
    },
    getAvailableCurrencies: security => {
      return uniq(compact([security.BaseCurrency, security.QuoteCurrency])).map(currency => ({
        currency: createRFQAssetCurrency(currency!),
        label: currency,
      }));
    },
  },
  compound: {
    type: 'compound',
    isMyType: security => isCompound(security) ?? false,
    onSymbolChanged: state => {
      state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(RFQ_UNITS_CURRENCY);
    },
    getAvailableCurrencies: () => {
      return [{ currency: RFQ_UNITS_CURRENCY, label: RFQ_UNITS_CURRENCY.label }];
    },
  },
  predictionMarket: {
    type: 'predictionMarket',
    isMyType: security => SecurityTypesUtils.PredictionOutcome.isProductType(security),
    // Only Contracts is supported for prediction RFQs for now.
    onSymbolChanged: state => {
      if (!rfqCurrencyEquals(state.form.rfqCurrencyField.value, RFQ_CONTRACTS_CURRENCY)) {
        state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(RFQ_CONTRACTS_CURRENCY, true);
      }
    },
    getAvailableCurrencies: () => {
      return [{ currency: RFQ_CONTRACTS_CURRENCY, label: RFQ_CONTRACTS_CURRENCY.label }];
    },
  },
  otcRepo: {
    type: 'otcRepo',
    isMyType: security => security.SubProductType === SubProductTypeEnum.OTCRepo,
    onSymbolChanged: (state, security) => {
      state.form.rfqCurrencyField = state.form.rfqCurrencyField.updateValue(
        getDefaultOTCRepoRFQCurrency(RFQFormCurrencies.otcRepo.getAvailableCurrencies(security)),
        true
      );
    },
    getAvailableCurrencies: security => {
      const productDetails = getOTCRepoProductDetails(security);
      return getOTCRepoQuantityCurrencyOptions({
        cashCurrency: productDetails?.BorrowedAsset,
        collateralAsset: productDetails?.RepoAsset,
      });
    },
  },
} as const;

export function getRFQFormCurrencySpec(security: Security): RFQFormCurrencySpec {
  const spec = values(RFQFormCurrencies).find(spec => spec.isMyType?.(security));
  return spec ?? RFQFormCurrencies.default;
}
